import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbItem } from '../../Common/Breadcrumbs';
import PlayersHeader from '../../Common/PlayersHeader';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { DeleteModal } from '../../Common/DeleteModal';
import imageCompression from 'browser-image-compression';
import axios from 'axios';
import Select from 'react-select';
import './PlayerManagement.css';
import '../../components/PlayerComponents.css';
import { getImageUrl } from '../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../components/PlayerAssetPreviewModal';
import { downloadFileFromUrl } from '../../utils/fileDownload';

// Import the SFL logo
import sflLogo from '../../assets/images/SFL Logo Blue.png';

// Import player management components and services
import PlayerForm from '../../components/PlayerForm';
import PlayerList from '../../components/PlayerList';
import { 
  getPlayersByTeamId, 
  getPlayerById,
  addPlayer, 
  updatePlayer, 
  deletePlayer,
  Player as ApiPlayer,
  AddPlayerRequest,
  UpdatePlayerRequest
} from '../../api/club/playerService';
import { PlayerFormData } from '../../types/Player';
import { getAgeCategoriesFromTournaments } from '../../api/club/teamService';
import { PLAYER_ASSETS_ENABLED } from '../../constants/featureFlags';
import { getPaymentPreview, PaymentPreviewResponse } from '../../api/tournament/paymentService';

interface Player extends ApiPlayer {
  team: string;
  verifyStat: 'Approved' | 'Referred Back' | 'Pending';
  paymentStat: 'Paid' | 'Not Paid' | 'Partial';
  amountPaid: number;
  amountDue: number;
  isSelected: boolean;
  qrCodeUrl?: string;
  playerIdCardUrl?: string;
}

interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
  registration?: string | null; // Add registration field for existing registrations
}

const PlayerManagement: React.FC = () => {
  const { id: tournamentId, teamId } = useParams<{ id: string; teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're in My Club context (no tournamentId) or Tournament context
  const isMyClubContext = !tournamentId;
  
  // State management
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [apiPlayers, setApiPlayers] = useState<ApiPlayer[]>([]);
  const [newlyAddedPlayers, setNewlyAddedPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [editingApiPlayer, setEditingApiPlayer] = useState<ApiPlayer | null>(null);
  const [navigationSource, setNavigationSource] = useState<'add' | 'manage'>('manage');
  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [usingRealPaymentData, setUsingRealPaymentData] = useState(false);
  const [assetPreviewConfig, setAssetPreviewConfig] = useState<{ url: string; type: PlayerAssetType; playerName: string; playerId?: string } | null>(null);
  
  // Record payment mode detection
  const isRecordPayment = location.state?.isRecordPayment || false;
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [ageCategoryFilter, setAgeCategoryFilter] = useState('all');
  
  // Payment preview fetching for record payment mode
  const fetchPaymentPreviewForRecordPayment = async (selectedPlayers: Player[]) => {
    if (isRecordPayment && team && tournamentId) {
      try {
        // ✅ UNIFIED API LOGIC: Always use the same API endpoint
        let response;
        
        const ageCategory = team.ageGroups?.[0] || 'U10';
        console.log('🔍 fetchPaymentPreviewForRecordPayment - Using unified payment-preview API');
        console.log('🔍 Tournament ID:', tournamentId);
        console.log('🔍 Team ID:', team._id);
        console.log('🔍 Registration ID:', team.registration || null);
        console.log('🔍 Age Category:', ageCategory);
        console.log('🔍 Selected Player IDs:', selectedPlayers.map(p => p._id));
        
        response = await getPaymentPreview(
          tournamentId,
          team._id,
          {
            registrationId: team.registration || null, // Always send registrationId (null or actual ID)
            ageCategory,
            players: selectedPlayers.map(p => p._id)
          }
        );
        
        return response.data;
      } catch (error) {
        console.error('Error fetching payment preview for record payment:', error);
        throw error;
      }
    }
    return null;
  };

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalPlayer, setDeleteModalPlayer] = useState<Player | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filter players based on search term and age category
  const filteredPlayers = players.filter(player => {
    const matchesSearch = searchTerm === '' || 
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.team.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAgeCategory = ageCategoryFilter === 'all' || 
      player.ageCategory === ageCategoryFilter;
    
    return matchesSearch && matchesAgeCategory;
  });

  const editingPlayerQrUrl = editingApiPlayer?.qrCode ? getImageUrl(editingApiPlayer.qrCode) : '';
  const editingPlayerIdCardUrl = editingApiPlayer?.playerIdCard ? getImageUrl(editingApiPlayer.playerIdCard) : '';
  const editingAssetPlayer = editingApiPlayer
    ? {
        name: editingApiPlayer.name,
        playerId: editingApiPlayer.playerId,
        qrCodeUrl: editingPlayerQrUrl,
        playerIdCardUrl: editingPlayerIdCardUrl
      }
    : null;

  const getAssetUrlFromPlayer = (player: { qrCodeUrl?: string; playerIdCardUrl?: string }, type: PlayerAssetType) =>
    type === 'qrCode' ? player.qrCodeUrl : player.playerIdCardUrl;

  const openAssetPreview = (player: { qrCodeUrl?: string; playerIdCardUrl?: string; name: string; playerId?: string }, type: PlayerAssetType) => {
    const url = getAssetUrlFromPlayer(player, type);
    if (!url) return;
    setAssetPreviewConfig({ url, type, playerName: player.name, playerId: player.playerId });
  };

  const downloadAsset = (player: { qrCodeUrl?: string; playerIdCardUrl?: string; name: string; playerId?: string }, type: PlayerAssetType) => {
    const url = getAssetUrlFromPlayer(player, type);
    if (!url) return;

    const formattedName = player.playerId || player.name.replace(/\s+/g, '-').toLowerCase();
    const filename =
      type === 'qrCode'
        ? `${formattedName || 'player'}-qr-code.png`
        : `${formattedName || 'player'}-id-card.pdf`;

    downloadFileFromUrl(url, filename);
  };
  
  // Form data
  const [playerFormData, setPlayerFormData] = useState({
    name: '',
    team: '',
    dob: '',
    ageCategory: '',
    profilePic: '',
    idProof: '',
    consentForm: ''
  });

  // File input refs
  const playerImageRef = useRef<HTMLInputElement>(null);
  const idProofRef = useRef<HTMLInputElement>(null);
  const consentFormRef = useRef<HTMLInputElement>(null);

  // Age category options
  const ageCategoryOptions = [
    { value: 'U7', label: 'U7' },
    { value: 'U10', label: 'U10' },
    { value: 'U11', label: 'U11' },
    { value: 'U12', label: 'U12' },
    { value: 'U13', label: 'U13' },
    { value: 'U14', label: 'U14' },
    { value: 'U15', label: 'U15' },
    { value: 'U16', label: 'U16' },
    { value: 'U17', label: 'U17' },
    { value: 'U18', label: 'U18' }
  ];

  // Mock data for players
  const mockPlayers: Player[] = [
    {
      _id: '1',
      name: 'Meera Kapoor',
      profilePic: '',
      dob: '25-07-2000',
      ageCategory: 'U12',
      verificationStatus: 'approved',
      createdAt: new Date().toISOString(),
      team: 'RS',
      verifyStat: 'Approved',
      paymentStat: 'Paid',
      amountPaid: 1000,
      amountDue: 0,
      isSelected: false
    },
    {
      _id: '2',
      name: 'Rayan Singh',
      profilePic: '',
      dob: '25-07-2000',
      ageCategory: 'U12',
      verificationStatus: 'referred_back',
      createdAt: new Date().toISOString(),
      team: 'RS',
      verifyStat: 'Referred Back',
      paymentStat: 'Partial',
      amountPaid: 500,
      amountDue: 500,
      isSelected: false
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Clear player selections when component loads (for manage players mode)
  useEffect(() => {
    // Always clear selections when component loads to ensure no players are pre-selected
    if (players.length > 0) {
      setPlayers(prev => prev.map(player => ({
        ...player,
        isSelected: false
      })));
    }
  }, [players.length]); // Trigger when number of players changes

  const loadData = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 PlayerManagement - Component loaded with location state:', location.state);
      console.log('🔍 PlayerManagement - Navigation source:', location.state?.source);
      
      // Load age categories
      await loadAgeCategories();
      
      // Determine navigation source
      const source = location.state?.source || 'manage';
      setNavigationSource(source);
      
      // Show form if coming from "Add Players"
      if (source === 'add') {
        setShowPlayerForm(true);
      }

      // Load team data from location state or mock data
      console.log('🔍 PlayerManagement - Location state:', location.state);
      console.log('🔍 PlayerManagement - Team ID from params:', teamId);
      
      if (location.state?.team) {
        const teamData = location.state.team;
        console.log('🔍 PlayerManagement - Team data from location state:', teamData);
        
        // Ensure logo URL is properly constructed
        const logoUrl = teamData.logo ? getImageUrl(teamData.logo) : '';
        
        setTeam({
          ...teamData,
          logo: logoUrl
        });
      } else {
        console.log('🔍 PlayerManagement - No team data in location state, redirecting to team registration');
        // ✅ FIXED: Instead of using dummy data, redirect to team registration
        navigate(`/tournaments/${tournamentId}/register`, {
          state: { 
            error: 'Team data not found. Please select a team from the team registration page.',
            tournament: location.state?.tournament 
          }
        });
        return;
      }

      // Load players from API if teamId is available
      if (teamId) {
        await loadTeamPlayers();
      } else {
      setPlayers(mockPlayers);
      }
      
      setNewlyAddedPlayers(mockPlayers.slice(0, 2)); // First 2 players as newly added
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamPlayers = async () => {
    try {
      if (!teamId) {
        console.error('No team ID available');
        setPlayers(mockPlayers);
        return;
      }
      
      const playersData = await getPlayersByTeamId(teamId, tournamentId);
      setApiPlayers(playersData);
      
      // Debug: Log player data to see what profile pictures are being returned
      console.log('🔍 Players data from API:', playersData);
      playersData.forEach((player, index) => {
        console.log(`🔍 Player ${index + 1} (${player.name}) profilePic:`, player.profilePic);
        console.log(`🔍 Player ${index + 1} (${player.name}) payment data:`, {
          amountPaid: player.amountPaid,
          amountDue: player.amountDue,
          verificationStatus: player.verificationStatus
        });
      });
      
      // Get real payment data for accurate amountDue calculations
      // NOTE: We keep this for other purposes (like payment preview) but don't override API amountDue values
      let realPaymentData: { perPlayerFee: number; totalAmount: number } | null = null;
      
      if (tournamentId && playersData.length > 0) {
        try {
          console.log('🔍 Fetching real payment data for accurate amountDue...');
          const ageCategory = team?.ageGroups?.[0] || 'U12';
          const playerIds = playersData.map(player => player._id);
          
          const paymentPreviewRequest = {
            registrationId: null,
            ageCategory: ageCategory,
            players: playerIds
          };
          
          // Real Payment Data Enhancement:
          // - Calls payment preview API to get accurate perPlayerFee from tournament
          // - Calculates amountDue as: perPlayerFee - amountPaid
          // - Falls back to API data or default if payment preview fails
          // - Shows visual indicators when real data is used
          
          const paymentPreviewResponse = await getPaymentPreview(
            tournamentId,
            teamId,
            paymentPreviewRequest
          );
          
          if (paymentPreviewResponse && paymentPreviewResponse.data) {
            realPaymentData = {
              perPlayerFee: paymentPreviewResponse.data.perPlayerFee || 0,
              totalAmount: paymentPreviewResponse.data.totalAmount || 0
            };
            console.log('🔍 Real payment data fetched:', realPaymentData);
            setUsingRealPaymentData(true);
          }
        } catch (paymentError) {
          console.log('🔍 Could not fetch real payment data, using fallback:', paymentError);
          setUsingRealPaymentData(false);
          // Continue with fallback data
        }
      }
      
      // Convert API players to Player format with proper profile picture handling
      // DISPLAY RAW API DATA: Show exactly what the API returns for amountPaid and amountDue
      // No overrides or modifications - display API data as-is
      // This ensures we show the exact data from /api/v1/club/team/list-players/{teamId}
      const convertedPlayers: Player[] = playersData.map(apiPlayer => {
        // Use API data directly without any overrides
        const amountPaid = apiPlayer.amountPaid || 0;
        const amountDue = apiPlayer.amountDue || 0;
        
        console.log('🔍 Using raw API data for player:', {
          playerName: apiPlayer.name,
          apiAmountPaid: apiPlayer.amountPaid,
          apiAmountDue: apiPlayer.amountDue,
          finalAmountPaid: amountPaid,
          finalAmountDue: amountDue
        });
        
        // Determine payment status based on API data
        let paymentStat: 'Paid' | 'Not Paid' | 'Partial';
        if (amountPaid > 0 && amountDue === 0) {
          paymentStat = 'Paid'; // Player has paid full amount
        } else if (amountPaid > 0 && amountDue > 0) {
          paymentStat = 'Partial'; // Player has paid some amount but still has dues
        } else {
          paymentStat = 'Not Paid'; // Player has not paid any amount
        }
        
        // Determine verification status
        let verifyStat: 'Approved' | 'Referred Back' | 'Pending';
        if (apiPlayer.verificationStatus === 'approved') {
          verifyStat = 'Approved';
        } else if (apiPlayer.verificationStatus === 'referred_back') {
          verifyStat = 'Referred Back';
        } else {
          verifyStat = 'Pending';
        }
        
        // Payment Status Logic based on API data:
        // - If amountPaid > 0 AND amountDue === 0: Player is marked as 'Paid' and cannot be selected
        // - If amountPaid > 0 AND amountDue > 0: Player is marked as 'Partial' and cannot be selected
        // - If amountPaid === 0: Player is marked as 'Not Paid' and can be selected
        
        console.log('🔍 Player payment status from API data:', {
          playerName: apiPlayer.name,
          amountPaid,
          amountDue,
          paymentStat,
          dataSource: 'Raw API Data'
        });
        
        const finalPlayer = {
          ...apiPlayer, // Include all standard ApiPlayer fields
          qrCodeUrl: apiPlayer.qrCode ? getImageUrl(apiPlayer.qrCode) : '',
          playerIdCardUrl: apiPlayer.playerIdCard ? getImageUrl(apiPlayer.playerIdCard) : '',
          team: team?.name || 'Unknown Team',
          verifyStat: verifyStat,
          paymentStat: paymentStat,
          amountPaid: amountPaid,
          amountDue: amountDue,
          isSelected: false // Players should not be selected by default
        };
        
        console.log('🔍 Final player data (raw API):', {
          playerName: apiPlayer.name,
          finalAmountPaid: finalPlayer.amountPaid,
          finalAmountDue: finalPlayer.amountDue,
          finalPaymentStat: finalPlayer.paymentStat
        });
        
        return finalPlayer;
      });
      
      setPlayers(convertedPlayers);
      console.log('Loaded players from API:', convertedPlayers);
    } catch (error) {
      console.error('Failed to load players from API, using mock data:', error);
      setPlayers(mockPlayers);
    }
  };

  const loadAgeCategories = async () => {
    try {
      // Pass the tournament ID to get age categories specific to this tournament
      const categories = await getAgeCategoriesFromTournaments(tournamentId || undefined);
      setAgeCategories(categories);
    } catch (error) {
      console.error('❌ Failed to load age categories:', error);
      // Set default categories if API fails
      const defaultCategories = ['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18'];
      console.log('🔄 Using default age categories:', defaultCategories);
      setAgeCategories(defaultCategories);
    }
  };



  // Form handlers
  const handleAddPlayer = () => {
    if (!teamId) {
      alert('No team selected. Please select a team first.');
      return;
    }
    
    setEditingPlayer(null);
    setEditingApiPlayer(null);
    setShowPlayerForm(true);
    setSubmitError(null);
    
    // Scroll to top smoothly after form is shown
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleEditPlayer = async (player: Player) => {
    try {
      // Fetch complete player data including documents
      const completePlayerData = await getPlayerById(player._id);
      console.log('Complete player data fetched for tournament:', completePlayerData);
      
      // Convert to ApiPlayer for editing
      const apiPlayer: ApiPlayer = {
        _id: completePlayerData._id,
        name: completePlayerData.name,
        profilePic: completePlayerData.profilePic,
        dob: completePlayerData.dob,
        ageCategory: completePlayerData.ageCategory,
        parentName: completePlayerData.parentName, // Include parent name for prefill
        verificationStatus: completePlayerData.verificationStatus,
        createdAt: completePlayerData.createdAt,
        documents: completePlayerData.documents || [],
        qrCode: completePlayerData.qrCode,
        playerIdCard: completePlayerData.playerIdCard
      };
      
      setEditingPlayer(player);
      setEditingApiPlayer(apiPlayer);
      setShowPlayerForm(true);
      setSubmitError(null);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Failed to fetch complete player data for tournament:', error);
      // Fallback to basic player data if API fails
      const originalApiPlayer = apiPlayers.find(p => p._id === player._id);
      
      const apiPlayer: ApiPlayer = {
        _id: player._id,
        name: player.name,
        profilePic: player.profilePic,
        dob: player.dob,
        ageCategory: player.ageCategory,
        parentName: originalApiPlayer?.parentName || '', // Include parent name from original data
        verificationStatus: player.verifyStat === 'Approved' ? 'approved' : 
                           player.verifyStat === 'Referred Back' ? 'referred_back' : 'pending',
        createdAt: new Date().toISOString(),
        documents: originalApiPlayer?.documents || [],
        qrCode: player.qrCode || originalApiPlayer?.qrCode,
        playerIdCard: player.playerIdCard || originalApiPlayer?.playerIdCard
      };
      
      setEditingPlayer(player);
      setEditingApiPlayer(apiPlayer);
      setShowPlayerForm(true);
      setSubmitError(null);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const handleSavePlayer = async (playerData: PlayerFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      if (!teamId) {
        throw new Error('No team selected');
      }

      if (editingApiPlayer) {
      // Update existing player
        const updateData: UpdatePlayerRequest = {
          name: playerData.name,
          profilePic: playerData.profilePic,
          dob: playerData.dob,
          ageCategory: playerData.ageCategory,
          parentName: playerData.parentName,
          documents: playerData.documents
        };

        const response = await updatePlayer(editingApiPlayer._id, updateData);
        
        if (response.success) {
          // Reload data to get updated player list
          await loadData();
          setShowPlayerForm(false);
          setEditingPlayer(null);
          setEditingApiPlayer(null);
        }
    } else {
      // Add new player
        const addData: AddPlayerRequest = {
          name: playerData.name,
          profilePic: playerData.profilePic,
          dob: playerData.dob,
          ageCategory: playerData.ageCategory,
          parentName: playerData.parentName,
          documents: playerData.documents
        };

        const response = await addPlayer(teamId, addData);
        
        if (response.success) {
          // Reload data to get updated player list
          await loadData();
          setShowPlayerForm(false);
        }
      }
    } catch (error: any) {
      console.error('Failed to save player:', error);
      setSubmitError(error.message || 'Failed to save player. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelPlayer = () => {
    setShowPlayerForm(false);
    setEditingPlayer(null);
    setEditingApiPlayer(null);
    setSubmitError(null);
  };

  const handleDeletePlayer = async (player: Player) => {
    setDeleteError(null);
    setDeleteModalPlayer(player);
    setShowDeleteModal(true);
  };

  const confirmDeletePlayer = async () => {
    if (deleteModalPlayer) {
      try {
        setIsDeleting(true);
        setDeleteError(null);
        
        // Call the delete API
        await deletePlayer(deleteModalPlayer._id);
        
        // Success - continue with cleanup
        // Remove player from both state arrays
        const updatedPlayers = players.filter(p => p._id !== deleteModalPlayer._id);
        const updatedApiPlayers = apiPlayers.filter(p => p._id !== deleteModalPlayer._id);
        
        setPlayers(updatedPlayers);
        setApiPlayers(updatedApiPlayers);
        
        // Close modal
        setShowDeleteModal(false);
        setDeleteModalPlayer(null);
        setDeleteError(null);
      } catch (error: any) {
        console.error('Error deleting player:', error);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete player. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteModalPlayer(null);
    setDeleteError(null);
  };

  const handleViewPlayer = async (player: Player) => {
    try {
      // Fetch complete player data including documents
      const completePlayerData = await getPlayerById(player._id);
      console.log('Complete player data for tournament view:', completePlayerData);
      
      // Convert to ApiPlayer for viewing
      const apiPlayer: ApiPlayer = {
        _id: completePlayerData._id,
        name: completePlayerData.name,
        profilePic: completePlayerData.profilePic,
        dob: completePlayerData.dob,
        ageCategory: completePlayerData.ageCategory,
        parentName: completePlayerData.parentName, // Include parent name for viewing
        verificationStatus: completePlayerData.verificationStatus,
        createdAt: completePlayerData.createdAt,
        documents: completePlayerData.documents || []
      };
      
      setEditingPlayer(player);
      setEditingApiPlayer(apiPlayer);
      setShowPlayerForm(true);
      setSubmitError(null);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Failed to fetch complete player data for tournament view:', error);
      // Fallback to basic player data if API fails
      alert(`Player: ${player.name}\nTeam: ${player.team}\nAge Category: ${player.ageCategory}\nStatus: ${player.verifyStat}`);
    }
  };

  const handlePlayerFormChange = (field: string, value: string) => {
    setPlayerFormData(prev => ({ ...prev, [field]: value }));
  };

  // File upload handlers
  const handlePlayerImageUpload = () => {
    playerImageRef.current?.click();
  };

  const handleIdProofUpload = () => {
    idProofRef.current?.click();
  };

  const handleConsentFormUpload = () => {
    consentFormRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert('The selected file is too large. Please choose a file smaller than 5MB.');
      return;
    }

    try {
      // For now, just update the form data with file name
      // In real implementation, upload to S3 and get URL
      setPlayerFormData(prev => ({ 
        ...prev, 
        [field]: file.name 
      }));
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    }
  };

  // Navigation handlers
  const handleGoBack = () => {
    // Check if we're in registered tournament context
    const isRegisteredTournamentContext = window.location.pathname.includes('/registered-tournaments/');
    
    if (isRegisteredTournamentContext) {
      // Navigate back to registered tournament detail page
      navigate(`/registered-tournaments/${tournamentId}`);
    } else if (isMyClubContext) {
      navigate('/my-club/team-information');
    } else {
      // Regular tournament context - go back to register teams listing
      navigate(`/tournaments/${tournamentId}/register`);
    }
  };

  const handleRecordPaymentFromPlayerManagement = async () => {
    try {
      setLoading(true);
      
      // Get team data from location state
      const { team: teamData, tournament } = location.state;
      
      if (!teamData || !tournament) {
        alert('Missing team or tournament data. Please go back and try again.');
        return;
      }
      
      // Get all players for the team (no selection needed for record payment)
      const allPlayerIds = players.map(player => player._id);
      
      if (allPlayerIds.length === 0) {
        alert('No players found for this team.');
        return;
      }
      
      // ✅ UNIFIED API LOGIC: Always use the same API endpoint
      let response;
      
      const ageCategory = teamData.ageGroups?.[0] || 'U10';
      console.log('🔍 PlayerManagement - Using unified payment-preview API');
      console.log('🔍 Tournament ID:', tournamentId);
      console.log('🔍 Team ID:', teamData._id);
      console.log('🔍 Registration ID:', teamData.registration || null);
      console.log('🔍 Age Category:', ageCategory);
      console.log('🔍 Player IDs:', allPlayerIds);
      
      response = await getPaymentPreview(
        tournamentId!,
        teamData._id,
        {
          registrationId: teamData.registration || null, // Always send registrationId (null or actual ID)
          ageCategory,
          players: allPlayerIds
        }
      );
      
      const paymentPreview = response.data;
      
      // Navigate to payment overview screen
      navigate(`/registered-tournaments/${tournamentId}/teams/${teamData._id}/record-payment`, {
        state: {
          team: teamData,
          tournament,
          allPlayers: allPlayerIds,
          paymentPreview,
          hasExistingRegistration: !!teamData.registration,
          registrationId: teamData.registration || null,
          isRecordPayment: true,
          paymentMode: 'offline'
        }
      });
      
    } catch (error: any) {
      console.error('Error initiating record payment:', error);
      alert(error.message || 'Failed to initiate record payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayments = async () => {
    // ✅ UNIFIED: Always use all players for payment (no selection needed)
    const allPlayers = players;
    
    if (allPlayers.length === 0) {
      alert('No players found for this team.');
      return;
    }

    try {
      // Show loading state
      setLoading(true);
      
      if (isRecordPayment) {
        // Handle record payment flow
        console.log('🔍 PlayerManagement - Record payment mode detected');
        
        // Fetch payment preview for record payment
        const paymentPreview = await fetchPaymentPreviewForRecordPayment(allPlayers);
        
        // Navigate to record payment page
        navigate(`/registered-tournaments/${tournamentId}/teams/${teamId}/record-payment`, {
          state: {
            team,
            tournament: location.state?.tournament,
            allPlayers: allPlayers.map(player => player._id), // ✅ UNIFIED: Always use all players
            selectedPlayers: allPlayers.map(player => player._id), // ✅ UNIFIED: Same as allPlayers for consistency
            paymentPreview,
            hasExistingRegistration: team?.registration !== null,
            registrationId: team?.registration || null,
            unpaidPlayerIds: allPlayers.map(player => player._id), // ✅ UNIFIED: Always use all players
            isRecordPayment: true,
            paymentMode: 'offline'
          }
        });
      } else {
        // Handle existing online payment flow
      // Check if team has existing registration
      const hasExistingRegistration = !!team?.registration;
      console.log('🔍 PlayerManagement - Team registration status:', {
        hasRegistration: hasExistingRegistration,
        registrationId: team?.registration,
        teamName: team?.name
      });
      
      let paymentPreviewResponse: PaymentPreviewResponse;
      
      // ✅ UNIFIED API LOGIC: Always use the same API endpoint
      const ageCategory = team?.ageGroups?.[0] || 'U12';
      console.log('🔍 PlayerManagement - Using unified payment-preview API');
      console.log('🔍 Tournament ID:', tournamentId);
      console.log('🔍 Team ID:', teamId);
      console.log('🔍 Registration ID:', team?.registration || null);
      console.log('🔍 Age Category:', ageCategory);
      console.log('🔍 All Players:', allPlayers.map(player => player._id));
      
      // Validate age category format (should be like "U10", "U12", etc.)
      if (!ageCategory.match(/^U\d+$/)) {
        console.warn('🔍 Warning: Age category format might not match expected format:', ageCategory);
        console.warn('🔍 Expected format: U10, U12, U14, etc.');
      }
      
      // Prepare payment preview request
      const paymentPreviewRequest = {
        registrationId: team?.registration || null, // Always send registrationId (null or actual ID)
        ageCategory: ageCategory,
        players: allPlayers.map(player => player._id) // ✅ UNIFIED: Always use all players
      };

      console.log('🔍 Payment preview request:', paymentPreviewRequest);

      // Call payment preview API
      paymentPreviewResponse = await getPaymentPreview(
        tournamentId!,
        teamId!,
        paymentPreviewRequest
      );

        console.log('🔍 Payment preview response:', paymentPreviewResponse);

      // Validate payment preview data before passing to PaymentPage
      const paymentData = paymentPreviewResponse.data;
      console.log('🔍 Validating payment data for PaymentPage:', {
        hasData: !!paymentData,
        totalAmount: paymentData?.totalAmount,
        fullAmount: paymentData?.fullAmount,
        perPlayerFee: paymentData?.perPlayerFee,
        playerCount: paymentData?.playerCount,
        amountPaid: paymentData?.amountPaid,
        minimumAmountToPay: paymentData?.minimumAmountToPay,
        balanceAmount: paymentData?.balanceAmount,
        hasExistingRegistration: hasExistingRegistration
      });

      // Navigate to payment page with appropriate data
      navigate(`/tournaments/${tournamentId}/payment`, {
        state: { 
          teamId: teamId,
          team: team,
          selectedPlayers: allPlayers, // ✅ UNIFIED: Always use all players
          source: 'playerManagement',
          tournament: location.state?.tournament || null,
          paymentPreview: paymentData, // Pass complete payment data
          hasExistingRegistration: hasExistingRegistration,
          registrationId: team?.registration || null,
          unpaidPlayerIds: allPlayers.map(player => player._id), // ✅ UNIFIED: Always use all players
          realPaymentData: true // Flag to indicate this is real payment data
        }
      });
      }
    } catch (error: any) {
      console.error('❌ Error fetching payment preview:', error);
      
      // Check if it's a 403 error (permission issue)
      if (error.message.includes('403') || error.message.includes('Access denied')) {
        console.log('🔍 Payment preview API not available or permission denied, using fallback');
        // Don't show alert for 403 errors, just proceed with fallback
      } else {
        // Show error message to user for other errors
        alert(`Failed to fetch payment details: ${error.message}. Proceeding with estimated payment.`);
      }
      
      // Fallback to existing flow with mock data
      if (isRecordPayment) {
        navigate(`/registered-tournaments/${tournamentId}/teams/${teamId}/record-payment`, {
          state: {
            team,
            tournament: location.state?.tournament,
            allPlayers: allPlayers.map(player => player._id), // ✅ UNIFIED: Always use all players
            selectedPlayers: allPlayers.map(player => player._id), // ✅ UNIFIED: Same as allPlayers
            hasExistingRegistration: team?.registration !== null,
            registrationId: team?.registration || null,
            unpaidPlayerIds: allPlayers.map(player => player._id), // ✅ UNIFIED: Always use all players
            isRecordPayment: true,
            paymentMode: 'offline'
          }
        });
      } else {
      navigate(`/tournaments/${tournamentId}/payment`, {
        state: { 
          teamId: teamId,
          team: team,
          selectedPlayers: allPlayers, // ✅ UNIFIED: Always use all players
          source: 'playerManagement',
          tournament: location.state?.tournament || null
        }
      });
      }
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const isFormValid = () => {
    return playerFormData.name.trim() !== '' && 
           playerFormData.team.trim() !== '' && 
           playerFormData.dob !== '' && 
           playerFormData.ageCategory !== '';
  };

  if (loading) {
    return (
      <div className="player-management-page">
        <Container fluid style={{ marginTop: '20px' }}>
          {/* Breadcrumbs */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs
              items={[
                { label: 'Tournaments', onClick: () => navigate('/tournaments') },
                { label: 'Register Teams', onClick: () => navigate(`/tournaments/${tournamentId}/register`) },
                { label: 'Loading...', active: true }
              ]}
            />
          </div>

          {/* Page Header Shimmer */}
          <div className="mb-4">
            <div className="d-flex align-items-center mb-3">
              <div className="shimmer-circle" style={{ width: '48px', height: '48px', marginRight: '16px' }}></div>
              <div className="shimmer-text" style={{ width: '200px', height: '32px' }}></div>
            </div>
            <div className="shimmer-text" style={{ width: '500px', height: '16px' }}></div>
          </div>
          
          {/* Filters Shimmer */}
          <div className="mb-4">
            <div className="d-flex gap-3">
              <div className="shimmer-text" style={{ width: '140px', height: '40px' }}></div>
              <div className="shimmer-text" style={{ width: '140px', height: '40px' }}></div>
            </div>
          </div>
          
          {/* Players Table Shimmer */}
          <ShimmerLoader type="table-rows" variant="player" rows={5} />
        </Container>
      </div>
    );
  }

  return (
    <div className="player-management-page">
      <Container fluid style={{ marginTop: '20px' }}>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs
            items={[
              { label: 'Tournaments', onClick: () => navigate('/tournaments') },
              { label: 'Register Teams', onClick: () => navigate(`/tournaments/${tournamentId}/register`) },
              { label: team?.name || 'Team Details', active: true }
            ]}
          />
        </div>

        {/* Page Header */}
        <PlayersHeader
          team={team}
          title="Players"
          description="Select an existing Player or create a new player to proceed with registration"
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          ageCategoryFilter={ageCategoryFilter}
          onAgeCategoryFilterChange={setAgeCategoryFilter}
          onAddPlayer={handleAddPlayer}
          onGoBack={handleGoBack}
          addButtonText="Add New Player"
          showFilters={true}
        />

        {/* Add/Edit Player Form - Show inline when adding/editing */}
        {showPlayerForm && (
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{editingPlayer ? 'Edit Player Details' : 'Add Player Details'}</h5>
                <Button 
                  variant="link" 
                  className="close-form-btn"
                  onClick={handleCancelPlayer}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </div>

              {PLAYER_ASSETS_ENABLED && editingAssetPlayer && (
                <div className="mb-4">
                  <h6 className="mb-3">Player Assets</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="asset-card">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h6 className="mb-1">QR Code</h6>
                            <p className="text-muted mb-0">Scan to verify player details instantly.</p>
                          </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            variant="outline-dark"
                            onClick={() => openAssetPreview(editingAssetPlayer, 'qrCode')}
                            disabled={!editingAssetPlayer.qrCodeUrl}
                          >
                            View
                          </Button>
                          <Button
                            variant="success"
                            onClick={() => downloadAsset(editingAssetPlayer, 'qrCode')}
                            disabled={!editingAssetPlayer.qrCodeUrl}
                          >
                            Download
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="asset-card">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <h6 className="mb-1">Player ID Card</h6>
                            <p className="text-muted mb-0">Share or print this ID for match-day checks.</p>
                          </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            variant="outline-dark"
                            onClick={() => openAssetPreview(editingAssetPlayer, 'playerIdCard')}
                            disabled={!editingAssetPlayer.playerIdCardUrl}
                          >
                            View
                          </Button>
                          <Button
                            variant="success"
                            onClick={() => downloadAsset(editingAssetPlayer, 'playerIdCard')}
                            disabled={!editingAssetPlayer.playerIdCardUrl}
                          >
                            Download
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              <PlayerForm
                player={editingApiPlayer}
                teamId={teamId || ''}
                teamName={team?.name || 'Unknown Team'}
                ageCategories={ageCategories}
                showTeamField={false} // Hide team field in tournament context
                teamAgeCategories={team?.ageGroups} // Pass team's age categories array
                hideAgeCategoryField={!editingApiPlayer} // Hide age category field when adding new player
                tournamentId={tournamentId} // 🆕 Enable tournament-based DOB validation
                onSubmit={handleSavePlayer}
                onCancel={handleCancelPlayer}
                isSubmitting={isSubmitting}
                submitError={submitError}
                isReadOnly={editingApiPlayer?.verificationStatus === 'approved' || location.state?.mode === 'view'}
              />
            </Card.Body>
          </Card>
        )}



        {/* Player Lineups */}
        <Card className="players-card mb-4">
          <Card.Body>
            <h5 className="card-title mb-3">Player Lineups</h5>
            <div className="player-table-responsive">
              <Table className="teams-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Name</th>
                    <th>DOB</th>
                    <th>Verify Status</th>
                    {PLAYER_ASSETS_ENABLED && (
                      <>
                        <th className="asset-column-header qr-code-column">QR Code</th>
                        <th className="asset-column-header player-id-card-column">Player ID Card</th>
                      </>
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlayers.map((player) => (
                    <tr key={player._id}>
                      <td>
                        <div className="player-profile-small">
                          {player.profilePic && player.profilePic !== '' && !player.profilePic.includes('association') && !player.profilePic.includes('banner') ? (
                            <img 
                              src={getImageUrl(player.profilePic)}
                              alt={player.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                console.log('🔍 Player profile image error for:', player.name, 'profilePic:', player.profilePic);
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('d-none');
                              }}
                              onLoad={(e) => {
                                console.log('🔍 Player profile image loaded successfully for:', player.name, 'profilePic:', player.profilePic);
                              }}
                            />
                          ) : null}
                          {(!player.profilePic || player.profilePic === '' || player.profilePic.includes('association') || player.profilePic.includes('banner')) && (
                            <div className="default-profile-icon-small">
                              <i className="ri-user-line"></i>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{player.name}</td>
                      <td>{player.dob}</td>
                      <td>
                        <Badge 
                          bg={player.verifyStat === 'Approved' ? 'success' : player.verifyStat === 'Referred Back' ? 'warning' : 'warning'}
                          className="verify-badge"
                        >
                          {player.verifyStat}
                        </Badge>
                      </td>
                      {PLAYER_ASSETS_ENABLED && (
                        <>
                          <td className="asset-column-cell qr-code-column">
                            <div className="asset-action-wrapper">
                              <button
                                type="button"
                                className="asset-btn asset-btn-view"
                                disabled={!player.qrCodeUrl}
                                onClick={() => openAssetPreview(player, 'qrCode')}
                                title="View"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              <button
                                type="button"
                                className="asset-btn asset-btn-download"
                                disabled={!player.qrCodeUrl}
                                onClick={() => downloadAsset(player, 'qrCode')}
                                title="Download"
                              >
                                <i className="ri-download-line"></i>
                              </button>
                            </div>
                          </td>
                          <td className="asset-column-cell player-id-card-column">
                            <div className="asset-action-wrapper">
                              <button
                                type="button"
                                className="asset-btn asset-btn-view"
                                disabled={!player.playerIdCardUrl}
                                onClick={() => openAssetPreview(player, 'playerIdCard')}
                                title="View"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              <button
                                type="button"
                                className="asset-btn asset-btn-download"
                                disabled={!player.playerIdCardUrl}
                                onClick={() => downloadAsset(player, 'playerIdCard')}
                                title="Download"
                              >
                                <i className="ri-download-line"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="link"
                            size="sm"
                            className="btn-edit"
                            title="Edit Player"
                            onClick={() => handleEditPlayer(player)}
                            disabled={player.verifyStat === 'Approved'}
                          >
                            <i className="ri-edit-line"></i>
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="btn-delete"
                            title="Delete Player"
                            onClick={() => handleDeletePlayer(player)}
                            disabled={player.verifyStat === 'Approved'}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {/* Empty State - Show when no players are found */}
            {filteredPlayers.length === 0 && (
              <div className="text-center py-5">
                <i className="ri-user-line empty-icon" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}></i>
                <h5 className="text-muted mb-2">
                  {location.state?.source === 'add' 
                    ? 'No players added yet' 
                    : 'No players found for this team'
                  }
                </h5>
                <p className="text-muted mb-3">
                  {location.state?.source === 'add' 
                    ? 'Click "Add Player" to start adding players to this team.'
                    : 'This team currently has no registered players.'
                  }
                </p>
                {location.state?.source === 'add' && (
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => setShowPlayerForm(true)}
                  >
                    <i className="ri-add-line me-1"></i>
                    Add Player
                  </Button>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Action Buttons Section - Go back on left, Proceed on right */}
        <div className="action-buttons-section">
          <div className="left-section">
            <Button 
              variant="link" 
              className="go-back-btn"
              onClick={handleGoBack}
            >
              {(() => {
                const isRegisteredTournamentContext = window.location.pathname.includes('/registered-tournaments/');
                if (isRegisteredTournamentContext) {
                  return '← Go back to Registered Tournament Detail';
                } else if (isMyClubContext) {
                  return '← Go back to Team Information';
                } else {
                  return '← Go back to Register Teams';
                }
              })()}
            </Button>
          </div>
          {!isMyClubContext && !isRecordPayment && (
            <div className="right-section">
              <Button 
                variant="dark" 
                onClick={handleProceedToPayments}
                disabled={players.length === 0}
                title={players.length === 0 ? 
                  "No players available for payment" : 
                  "Proceed to payment for all players in this team"}
              >
                Proceed to Payment
              </Button>
            </div>
          )}
          {!isMyClubContext && isRecordPayment && (
            <div className="right-section">
              <Button 
                variant="dark" 
                onClick={handleRecordPaymentFromPlayerManagement}
                disabled={players.length === 0}
                title={players.length === 0 ? 
                  "No players available for payment recording" : 
                  "Record payment for all players in this team"}
              >
                Record Payment
              </Button>
            </div>
          )}
        </div>
      </Container>
      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeletePlayer}
        title="Delete Player"
        message={deleteError || `Are you sure you want to delete "${deleteModalPlayer?.name}"? This action cannot be undone.`}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={!!assetPreviewConfig}
          assetType={assetPreviewConfig?.type || 'qrCode'}
          assetUrl={assetPreviewConfig?.url}
          playerName={assetPreviewConfig?.playerName}
          onClose={() => setAssetPreviewConfig(null)}
          onDownload={() => {
            if (!assetPreviewConfig?.url) return;
            const formattedName =
              assetPreviewConfig.playerId ||
              assetPreviewConfig.playerName.replace(/\s+/g, '-').toLowerCase();
            const filename =
              assetPreviewConfig.type === 'qrCode'
                ? `${formattedName || 'player'}-qr-code.png`
                : `${formattedName || 'player'}-id-card.pdf`;
            downloadFileFromUrl(assetPreviewConfig.url, filename);
          }}
        />
      )}
    </div>
  );
};

export default PlayerManagement; 